<template>
  <div class="animated fadeIn">
    <b-card>
      <h1>Contacts</h1>
      <div class="d-inline-flex mb-4 mx-n2 text-center">
        <div class="mx-2 py-2 px-4 bg-light rounded">
          <div>Username</div>
          <div class="font-xl font-weight-bold">
            {{ user.name }}
          </div>
        </div>
        <div class="mx-2 py-2 px-4 bg-light rounded">
          <div>Invite Code</div>
          <div class="font-xl font-weight-bold">{{ friend_code }}</div>
        </div>
      </div>

      <template v-if="pending.length">
        <h3>{{ pending.length | pluralize("pending contact") }}</h3>

        <b-table
          :fields="[
            { key: 'name', label: 'Username' },
            { key: 'email', label: 'Email' },
            { key: 'actions', label: 'Respond', class: 'text-center' }
          ]"
          :items="pending"
        >
          <template #cell(actions)="{ item }">
            <b-btn
              class="py-1 px-3 mx-1"
              variant="primary"
              @click="respondToContactRequest(item.id, true)"
            >
              <i class="icon-check" />
              accept
            </b-btn>

            <b-btn
              class="py-1 px-3 mx-1"
              variant="light"
              @click="respondToContactRequest(item.id, false)"
            >
              <i class="icon-close" />
              decline
            </b-btn>
          </template>
        </b-table>
      </template>

      <h3>{{ contacts.length | pluralize("contact") }}</h3>

      <b-table
        :fields="
          [
            { key: 'type', label: 'Type', thStyle: 'width: 0' },
            { key: 'name', label: 'Username' },
            { key: 'email', label: 'Email' },
            { key: 'nickname', label: 'Nickname' },
            { key: 'status', label: 'Status' },
            { key: 'actions', label: '', class: 'text-center' }
          ].slice(user.organization ? 0 : 1)
        "
        :items="contacts"
        show-empty
      >
        <template #empty>
          <div class="my-3 text-center font-lg">
            You have no contacts
          </div>
        </template>

        <template #cell(type)="{ item }">
          <b-badge
            v-if="user.organization"
            :variant="item.external ? 'secondary' : 'primary'"
          >
            {{ item.external ? "external" : "internal" }}
          </b-badge>
        </template>

        <template #cell(nickname)="{ item }">
          <div class="d-flex align-items-center">
            <div v-if="!editing[item.id]">
              {{ item.nickname }}
            </div>
            <b-input
              v-else
              v-model="item.nickname"
              style="max-width: 10em"
              autofocus
              @keydown.enter="toggleEditNickname(item)"
            />
            <b-btn
              class="ml-1 py-0"
              variant="link"
              @click="toggleEditNickname(item)"
            >
              <i :class="editing[item.id] ? 'icon-check' : 'icon-pencil'" />
            </b-btn>
          </div>
        </template>

        <template #cell(status)="{ item }">
          <b-badge v-if="!item.accepted" variant="warning">pending</b-badge>
          <b-badge v-else :variant="item.status ? 'primary' : 'secondary'">
            {{ item.status || "offline" }}
          </b-badge>
        </template>

        <template #cell(actions)="{ item }">
          <b-btn
            v-b-tooltip.hover="'Remove Contact'"
            class="py-0 px-2"
            variant="link"
            @click="deleteContact(item.id)"
          >
            <i class="icon-trash text-danger" />
          </b-btn>
        </template>
      </b-table>

      <b-btn
        v-if="user.organization"
        v-b-modal.add-org-contact
        class="mr-2 px-4"
        variant="primary"
      >
        Add Contact from Org
      </b-btn>

      <b-btn v-b-modal.add-contact class="px-4" variant="primary">
        Add {{ user.organization ? "External" : "" }} Contact
      </b-btn>
    </b-card>

    <b-modal
      ref="modal"
      id="add-contact"
      size="sm"
      centered
      hide-footer
      hide-header
      @show="code = ''"
    >
      <h3>Add Contact</h3>
      <form @submit.prevent="addContact(code)">
        <div class="mb-2">
          Enter the username, email, or invite code from the contact to add.
        </div>
        <NanInput
          v-model="code"
          label="Username, Email, or Invite Code"
          class="mb-3"
        />
        <div class="text-center">
          <b-btn :disabled="!code" class="px-4" type="submit" variant="primary">
            Add Contact
          </b-btn>
        </div>
      </form>
    </b-modal>

    <b-modal
      ref="modal"
      id="add-org-contact"
      size="md"
      centered
      hide-footer
      hide-header
      @show="getOrgMembers"
    >
      <h3>Add Contact from Org</h3>
      <b-input v-model="memberSearch" placeholder="search..." type="search" />
      <b-table
        :fields="[
          { key: 'name', label: 'Username' },
          { key: 'email', label: 'Email' },
          { key: 'actions', label: '', class: 'text-center' }
        ]"
        :items="orgMembers"
        :filter="memberSearch"
        show-empty
      >
        <template #empty>
          <div class="my-3 text-center">
            You have no other members in this organization
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <b-btn
            :disabled="isAdded(item)"
            class="p-0"
            variant="link"
            @click="addContact(item.email)"
          >
            <i v-if="isAdded(item)" class="icon-check text-success" />
            <i v-else class="icon-plus text-primary" />
          </b-btn>
        </template>
      </b-table>
      <b-btn class="px-4" @click="addAllOrgMembers" variant="primary">
        Add All
      </b-btn>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";
import API from "@/api";

export default {
  data: () => ({
    code: "",
    editing: {},
    memberSearch: "",
    orgMembers: []
  }),

  computed: {
    ...mapState(["user"]),
    ...mapState("contacts", ["contacts", "pending"]),

    friend_code() {
      const code = this.user.friend_code;
      return code.toUpperCase().replace(/^(.{3})/, "$1 ");
    }
  },

  created() {
    this.getContacts();
    this.getPendingContacts();
  },

  methods: {
    ...mapActions("contacts", ["getContacts", "getPendingContacts"]),

    async getOrgMembers() {
      const orgId = this.user.organization.id;
      const { success, results } = await API.getOrgDetails(orgId);
      if (!success) return;
      this.orgMembers = results.members.filter(m => m.id !== this.user.id);
    },

    async addAllOrgMembers() {
      await Promise.all(
        this.orgMembers.map(async member => {
          if (this.isAdded(member)) return;
          await API.addContact(member.email);
        })
      );
      this.getContacts();
    },

    async addContact(code) {
      const { success } = await API.addContact(code);
      if (!success) return;
      this.$notify({
        type: "success",
        text: "Contact added"
      });
      this.code = "";
      this.getContacts();
    },

    async deleteContact(id) {
      const confirm = await this.$bvModal.msgBoxConfirm(
        "Are you sure you want to remove this contact?",
        {
          title: "Remove Contact",
          okTitle: "Yes",
          okVariant: "danger",
          size: "sm",
          centered: true
        }
      );
      if (!confirm) return;

      const { success } = await API.deleteContact(id);
      if (!success) return;
      this.$notify({
        type: "success",
        text: "Contact removed"
      });
      this.getContacts();
    },

    async editContact(id, nickname) {
      const { success } = await API.editContact(id, nickname);
      if (!success) return;
      this.$notify({
        type: "success",
        text: "Contact updated"
      });
      this.getContacts();
    },

    isAdded(user) {
      return !!this.contacts.find(c => c.id === user.id);
    },

    async respondToContactRequest(id, accept) {
      const { success } = await API.respondToContactRequest(id, accept);
      if (!success) return;
      this.getPendingContacts();
      if (accept) this.getContacts();
    },

    toggleEditNickname(user) {
      const value = !this.editing[user.id];
      this.$set(this.editing, user.id, value);
      if (!value) this.editContact(user.id, user.nickname);
    }
  }
};
</script>
